import {
  eventMatches,
  type KeyEventLike,
  parseShortcut,
  shortcutHasPrimaryModifier,
  type Chord,
} from "./keyboard";
import type { Platform } from "./shortcut";
import { resolveShortcut, type ShortcutPreferences } from "./ShortcutProvider";

function identity(chord: Chord, platform: Platform) {
  return [
    chord.meta || (chord.mod && platform === "mac"),
    chord.ctrl || (chord.mod && platform !== "mac"),
    chord.alt,
    chord.shift,
    chord.key,
  ].join(":");
}

/** Validate user input more strictly than the permissive runtime parser. */
export function normalizeShortcut(input: string): string {
  const normalized = input
    .trim()
    .toLowerCase()
    .replace(/\s*\+\s*/g, "+")
    .replace(/\s+/g, " ");
  const tokens = normalized.split(" ");
  if (tokens.length > 3) throw new Error("Use at most three keys in a sequence.");
  for (const token of tokens) {
    const parts = token.split("+");
    const key = parts.pop() ?? "";
    if (
      !/^(?:[^\s+]|f(?:[1-9]|1[0-9]|2[0-4])|enter|return|escape|esc|space|tab|backspace|delete|del|home|end|pageup|pagedown|(?:arrow)?(?:up|down|left|right)|plus|minus)$/.test(
        key,
      )
    ) {
      throw new Error("Use a key like N, Enter, or F6. Separate a sequence with spaces.");
    }
    if (
      parts.some(
        (part) =>
          !["mod", "cmd", "command", "meta", "ctrl", "control", "alt", "option", "shift"].includes(
            part,
          ),
      )
    ) {
      throw new Error("Use modifiers such as Mod, Ctrl, Alt, or Shift joined with +.");
    }
  }
  parseShortcut(normalized);
  return normalized;
}

const reserved: Record<string, string> = {
  ...Object.fromEntries(
    Array.from({ length: 9 }, (_, i) => [`mod+${i + 1}`, "Switch browser tab"]),
  ),
  "mod+shift+h": "Browser history or home page",
  "mod+alt+i": "Browser developer tools",
  "mod+alt+j": "Browser developer tools",
  "mod+alt+c": "Browser developer tools",
  "mod+alt+left": "Previous browser tab",
  "mod+alt+right": "Next browser tab",
  "mod+n": "New browser window",
  "mod+w": "Close browser tab",
  "mod+t": "New browser tab",
  "mod+shift+t": "Reopen closed browser tab",
  "mod+shift+n": "Private browser window",
  "mod+shift+p": "Private browser window in Firefox",
  "mod+shift+d": "Bookmark all browser tabs",
  "mod+,": "Browser settings on macOS",
  "mod+l": "Browser address bar",
  "mod+r": "Reload page",
  "mod+shift+r": "Reload page",
  "mod+q": "Quit application",
  "mod+h": "Hide application or browser history",
  "mod+p": "Print page",
  "mod+s": "Save page",
  "mod+f": "Find in page",
  "mod+d": "Bookmark page",
  "mod+k": "Browser search or editor link insertion",
  "mod+shift+k": "Browser developer tools",
  "mod+shift+c": "Browser developer tools",
  "mod+shift+i": "Browser developer tools",
  "mod+shift+j": "Browser downloads or developer tools",
  "mod+shift+[": "Previous browser tab",
  "mod+shift+]": "Next browser tab",
  "mod+space": "System search or input switching",
  "mod+shift+space": "System assistant or input switching",
  "ctrl+space": "Input switching",
  "ctrl+alt+space": "System or accessibility shortcut",
  "ctrl+alt+f": "Accessibility shortcut",
  "ctrl+tab": "Next browser tab",
  "ctrl+shift+tab": "Previous browser tab",
  "alt+left": "Browser back",
  "alt+right": "Browser forward",
  "alt+f4": "Close window",
  "meta+tab": "System application switcher",
  "alt+tab": "System application switcher",
};
const editing = [
  "mod+e",
  "mod+shift+e",
  "mod+shift+x",
  "mod+shift+enter",
  "mod+.",
  "mod+shift+7",
  "mod+shift+8",
  "mod+shift+9",
  "mod+b",
  "mod+i",
  "mod+k",
  "mod+z",
  "mod+shift+z",
  "mod+y",
  "mod+a",
  "mod+c",
  "mod+x",
  "mod+v",
  "mod+shift+v",
  "mod+enter",
  "mod+shift+left",
  "mod+shift+right",
  "mod+shift+up",
  "mod+shift+down",
];

export function validateShortcut(
  id: string,
  input: string,
  preferences: Pick<ShortcutPreferences, "definitions" | "overrides">,
  platform: Platform,
): { shortcut?: string; error?: string; warning?: string } {
  let shortcut: string;
  try {
    shortcut = normalizeShortcut(input);
  } catch (error) {
    return { error: error instanceof Error ? error.message : "Invalid shortcut." };
  }
  const parsed = parseShortcut(shortcut);
  const keys = parsed.map((key) => identity(key, platform));
  const definition = preferences.definitions.find((item) => item.id === id);
  if (
    definition?.scope === "editor" &&
    (parsed.length !== 1 || !shortcutHasPrimaryModifier(parsed))
  ) {
    return {
      error: "Editor actions need a single shortcut with Cmd or Ctrl so typing stays unchanged.",
    };
  }
  const first = keys[0];
  const initial = parsed[0]!;
  if (
    !initial.mod &&
    !initial.ctrl &&
    !initial.meta &&
    !initial.alt &&
    ["c", "e", "x", "p", "j", "k", "d", "t", "1", "2", "3", "4", "f2"].includes(initial.key)
  ) {
    return { error: "This key is used by lists or rename controls. Choose another starting key." };
  }
  if (editing.some((binding) => identity(parseShortcut(binding)[0]!, platform) === first)) {
    return { error: "This shortcut is reserved for text editing. Choose another binding." };
  }
  if (
    parsed.some(
      (key) =>
        [
          "escape",
          "tab",
          "enter",
          "backspace",
          "delete",
          " ",
          "arrowup",
          "arrowdown",
          "arrowleft",
          "arrowright",
        ].includes(key.key) &&
        !key.mod &&
        !key.ctrl &&
        !key.meta &&
        !key.alt,
    )
  ) {
    return { error: "Keep navigation, activation, and dismissal keys available to controls." };
  }
  for (const other of preferences.definitions) {
    if (other.id === id) continue;
    const binding = resolveShortcut(preferences, other.id);
    if (!binding) continue;
    const candidate = parseShortcut(binding).map((key) => identity(key, platform));
    // Prefixes are collisions too: `g` would prevent every `g …` sequence.
    if (
      keys.slice(0, Math.min(keys.length, candidate.length)).every((key, i) => key === candidate[i])
    ) {
      return { error: `Conflicts with “${other.title}”. Change or disable that shortcut first.` };
    }
  }
  for (const [binding, description] of Object.entries(reserved)) {
    if (identity(parseShortcut(binding)[0]!, platform) === first) {
      return {
        shortcut,
        warning: `${description} uses this shortcut. Your browser or system may intercept it.`,
      };
    }
  }
  if (parsed.some((key) => (key.ctrl && key.alt) || (platform !== "mac" && key.meta))) {
    return {
      shortcut,
      warning:
        "This combination may conflict with accessibility, AltGr typing, or system shortcuts.",
    };
  }
  return { shortcut };
}

/** Keep vendor editor keymaps from taking browser commands (e.g. Mod+1 headings). */
export function shouldLeaveShortcutToBrowser(event: KeyEventLike, platform: Platform): boolean {
  if (editing.some((binding) => eventMatches(event, binding, platform))) return false;
  return Object.keys(reserved).some((binding) => eventMatches(event, binding, platform));
}
